import { Injectable } from "@angular/core";

import { combineLatest, Observable } from "rxjs";
import { distinctUntilChanged, map, shareReplay } from "rxjs/operators";

import { MenuItem } from "@dffedb/ui";
import { AktuelBrugerObserver, Bruger, Rolle } from "@e-forsyning/common/bruger";
import { AppserverInstallationCredentialsRepository } from "@e-forsyning/common/credentials";
import { UlaesteDokumenterService } from "@e-forsyning/common/edh";
import { DocumentNotificationService } from "@e-forsyning/common/push-notification";

import { EforsyningMenuerObserver } from "./eforsyning-menuer.observer";

@Injectable({ providedIn: "root" })
export class EforsyningMenuForSidebarObserver {
    private readonly _valueChanges: Observable<MenuItem[]>;

    constructor(
        private readonly eforsyningMenuerObserver: EforsyningMenuerObserver,
        private readonly installationCredentialsRepository: AppserverInstallationCredentialsRepository,
        private readonly aktuelBrugerObserver: AktuelBrugerObserver,
        private readonly ulaesteDokumenterService: UlaesteDokumenterService,
        private readonly documentNotificationService: DocumentNotificationService
    ) {
        const eforsyningNodeId$ = this.installationCredentialsRepository.valueChanges.pipe(
            map((c) => (c && c.forsyningNodeId) || 0),
            distinctUntilChanged()
        );
        const bruger$ = this.aktuelBrugerObserver.valueChanges;
        const menu$ = this.eforsyningMenuerObserver.valueChanges;
        const ulaesteDokumenter$ = this.ulaesteDokumenterService.hentAntalUlaesteDokumenter();

        this._valueChanges = combineLatest([menu$, eforsyningNodeId$, bruger$, ulaesteDokumenter$]).pipe(
            map(([menu, eforsyningNodeId, bruger, ulaesteDokumenter]) => this.bygMenu(menu, eforsyningNodeId, bruger, ulaesteDokumenter)),
            shareReplay(1)
        );
    }

    public get valueChanges(): Observable<MenuItem[]> {
        return this._valueChanges;
    }

    private bygMenu(menu: MenuItem, eforsyningNodeId: number, bruger: Bruger, ulaesteDokumenter: number): MenuItem[] {
        return eforsyningNodeId && bruger ? this.bygLoggetIndMenu(menu, eforsyningNodeId, bruger, ulaesteDokumenter) : this.bygLoggetUdMenu(menu);
    }

    private bygLoggetUdMenu(menu: MenuItem): MenuItem[] {
        return menu.children.length === 1
            ? [{ title: "Log ind", href: menu.children[0].href, adgangsstyret: false }]
            : menu.children.map((c) => ({ title: c.title, href: c.href, adgangsstyret: c.adgangsstyret }));
    }

    private bygLoggetIndMenu(menu: MenuItem, eforsyningNodeId: number, bruger: Bruger, ulaesteDokumenter: number): MenuItem[] {
        const eforsyningMenu = menu.children.find((i) => i.id === eforsyningNodeId);
        const fofMenuItems = this.genererFofMenuItems(eforsyningMenu, bruger, ulaesteDokumenter);
        const forbrugerMenuItems = this.genererForbrugerMenuItems(eforsyningMenu, bruger, ulaesteDokumenter);
        const loginOgLogoutMenuItems = this.genererLoginOgLogoutMenuItems(eforsyningMenu);
        return [...fofMenuItems, ...forbrugerMenuItems, ...loginOgLogoutMenuItems];
    }

    private genererFofMenuItems(eforsyning: MenuItem, bruger: Bruger, ulaesteDokumenter: number): MenuItem[] {
        const children = this.findFofMenuItems(eforsyning);
        return bruger && Rolle.erFofBruger(bruger.rolle) ? this.tilføjBadgesTilMenuItems(children, ulaesteDokumenter) : [];
    }

    private genererForbrugerMenuItems(eforsyning: MenuItem, bruger: Bruger, ulaesteDokumenter: number): MenuItem[] {
        const children = this.fjernLoginOgLogoutMenuItems(this.findForbrugerMenuItems(eforsyning));
        const childrenMedBadges = this.tilføjBadgesTilMenuItems(children, ulaesteDokumenter);
        return bruger && Rolle.erFofBruger(bruger.rolle) && !!childrenMedBadges.length ? [this.genererForbrugerUndermenu(childrenMedBadges)] : childrenMedBadges;
    }

    private genererForbrugerUndermenu(children: MenuItem[]): MenuItem {
        return {
            title: "Forbruger",
            icon: "menu-forbruger",
            adgangsstyret: false,
            children
        };
    }

    private genererLoginOgLogoutMenuItems(eforsyning: MenuItem): MenuItem[] {
        return this.findLoginOgLogoutMenuItems(this.findForbrugerMenuItems(eforsyning));
    }

    private findFofMenuItems(eforsyning: MenuItem): MenuItem[] {
        const item = eforsyning && eforsyning.children.find((c) => c.name === "eforsyning-fof-bruger");
        return (item && item.children) || [];
    }

    private findForbrugerMenuItems(eforsyning: MenuItem): MenuItem[] {
        const item = eforsyning && eforsyning.children.find((c) => c.name === "eforsyning-forbruger");
        return (item && item.children) || [];
    }

    private fjernLoginOgLogoutMenuItems(menuItems: MenuItem[]): MenuItem[] {
        const loginOgLogout = this.findLoginOgLogoutMenuItems(menuItems);
        return menuItems.filter((i) => !loginOgLogout.includes(i));
    }

    private findLoginOgLogoutMenuItems(menuItems: MenuItem[]): MenuItem[] {
        return menuItems.filter((i) => i.icon === "menu-login" || i.icon === "menu-logout");
    }

    private tilføjBadgesTilMenuItems(menuItems: MenuItem[], ulaesteDokumenter: number): MenuItem[] {
        return menuItems.map((item) => {
            // Check for "mine-dokumenter" in route, href, or name
            const isMineDokumenter =
                item.route === "/mine-dokumenter" ||
                item.href === "/mine-dokumenter" ||
                item.href === "#/mine-dokumenter" ||
                item.name === "mine-dokumenter" ||
                item.title === "Mine dokumenter";

            if (isMineDokumenter && ulaesteDokumenter > 0) {
                return { ...item, badge: ulaesteDokumenter };
            }

            // Check for notification center
            const isNotificationCenter =
                item.route === "/notifikationer" || item.href === "/notifikationer" || item.name === "notifikationer" || item.title === "Notifikationer";

            if (isNotificationCenter) {
                // Her kunne vi tilføje badge baseret på ulæste notifikationer
                // For nu returnerer vi bare item uden badge
                return item;
            }

            return item;
        });
    }
}
